import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { markInspectedFormSchema } from '@ekhodealer/ekho-common';
import { useAuth } from '@ekhodealer/ekho-common/auth-client';
import {
	Button,
	Checkbox,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { formatTimestampToDate } from '@common/lib/time';

import { useGetMarkInspected } from '../../../api/progress/useGetMarkInspected';
import { useMarkInspected } from '../../../api/progress/useMarkInspected';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkInspectedDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkInspectedDetail form component for marking a vehicle as inspected.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkInspectedDetail = ({ actionItem }: MarkInspectedDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);
	const { user } = useAuth();

	const markInspectedMutation = useMarkInspected(orderId);
	const { data: existingInspectionData, isLoading: isLoadingInspectionData } =
		useGetMarkInspected(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markInspectedFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing inspection data
	useEffect(() => {
		if (existingInspectionData?.isComplete) {
			reset({
				isConfirmed: true,
			});
		}
	}, [existingInspectionData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		if (!user?.uid) {
			toast({
				message: 'Unable to mark as inspected: user not authenticated',
				variant: 'error',
			});
			return;
		}

		try {
			await markInspectedMutation.mutateAsync({
				inspection: {
					isComplete: true,
					agent: user.uid,
				},
			});
			toast({
				message: 'Vehicle marked as inspected successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to mark vehicle as inspected';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm completion of pre-delivery inspection and testing per the OEM guidelines. Ensure all checks are met for quality and safety. If you encounter any issues, contact the OEM before proceeding.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingInspectionData ? (
					<SkeletonRect width='100%' height={80} />
				) : existingInspectionData?.isComplete ? (
					<Stack itemsSpacing='8'>
						<Group itemsSpacing='8'>
							<Text size='body' color='success'>
								✓ Inspection Complete
							</Text>
						</Group>
						{existingInspectionData.dateInspected && (
							<Text size='bodySmall' color='secondary'>
								Completed on{' '}
								{formatTimestampToDate(new Date(existingInspectionData.dateInspected).getTime()) ?? '—'}
							</Text>
						)}
						{existingInspectionData.notes && (
							<Text size='bodySmall' color='secondary'>
								Notes: {existingInspectionData.notes}
							</Text>
						)}
					</Stack>
				) : null}

				{/* Show form if not complete */}
				{!existingInspectionData?.isComplete && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group
										itemsSpacing='8'
										itemsAlignY='center'
										itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting || markInspectedMutation.isPending
											}
											ariaLabel='Confirm vehicle inspection is complete'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || markInspectedMutation.isPending}
							isDisabled={isLoadingInspectionData}>
							{actionItem.config.buttonTitle || 'Mark as inspected'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
